import React, { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Grid, Link, ListItem, ListItemIcon, ListItemText } from '@material-ui/core';
import EmailIcon from '@material-ui/icons/Email';

import {
  FeedKey,
  UserFeedFollowGroupFragment,
  Network,
  PublicUserProfileFragment,
  SocialAccount,
} from 'api';
import { Feed } from 'features/feed';
import { DEFAULT_SORT, FEED_LIMIT } from 'app-constants';
import { useFeatures } from 'hooks';
import {
  Layouts,
  ListWidget,
  ProfilesWidget,
  GroupsWidget,
  Queries,
  EmptyState,
  IEmptyState,
  LeaderboardWidget,
} from 'components';

import ProfileItem from './ProfileItem';

interface IProps {
  header: ReactNode;
  profile: PublicUserProfileFragment;
  users: PublicUserProfileFragment[];
  followedByUsers: PublicUserProfileFragment[];
  followedByCount: number;
  groups: UserFeedFollowGroupFragment[];
  emptyStateName: keyof IEmptyState;
}

const ProfileActivity: FC<IProps> = props => {
  const { t } = useTranslation();
  const { leaderboard } = useFeatures();
  const { profile, emptyStateName, users, groups, followedByUsers, followedByCount, header } =
    props;
  const variables = {
    sort: DEFAULT_SORT,
    key: FeedKey.Alluser,
    id: profile.id,
    limit: FEED_LIMIT,
  };

  function renderProfileItem(account: SocialAccount) {
    return <ProfileItem key={account.id} account={account} />;
  }

  function renderContact(socialAccounts: SocialAccount[]) {
    const twitterAccounts = socialAccounts.filter(conn => conn.provider === Network.Twitter);
    const linkedinAccounts = socialAccounts.filter(conn => conn.provider === Network.Linkedin);
    const hasTwitter = !!twitterAccounts.length;
    const hasLinkedIn = !!linkedinAccounts.length;

    return (
      <ListWidget color="primary" title={t('common:contact')}>
        {hasTwitter && twitterAccounts.map(renderProfileItem)}
        {hasLinkedIn && linkedinAccounts.map(renderProfileItem)}
        <ListItem disableGutters>
          <ListItemIcon>
            <EmailIcon color="action" />
          </ListItemIcon>
          <ListItemText
            disableTypography
            primary={
              <Link
                title={profile.email_address}
                variant="body2"
                display="block"
                href={`mailto:${profile.email_address}`}
                noWrap
              >
                {profile.email_address}
              </Link>
            }
          />
        </ListItem>
      </ListWidget>
    );
  }

  function renderFollowing() {
    if (!users.length) {
      return null;
    }

    return (
      <Grid item xs>
        <ProfilesWidget
          maxResults={8}
          maxWidth={160}
          title={t('common:following')}
          link={`/profile/${profile.id}/following`}
          results={users}
        />
      </Grid>
    );
  }

  function renderGroups() {
    if (!groups.length) {
      return null;
    }

    return (
      <Grid item xs>
        <GroupsWidget
          maxResults={8}
          maxWidth={160}
          title={t('common:group', { count: groups.length })}
          link={`/profile/${profile.id}/groups`}
          results={groups}
        />
      </Grid>
    );
  }

  function renderFollowers() {
    if (!followedByUsers.length) {
      return null;
    }

    return (
      <Grid item xs>
        <ProfilesWidget
          maxResults={8}
          maxWidth={160}
          link={`/profile/${profile.id}/followers`}
          results={followedByUsers}
          title={t('components:xFollower', { count: followedByCount })}
        />
      </Grid>
    );
  }

  return (
    <Layouts.AppMain2Column
      hideRightOnMobile
      desktopSplit="8/4"
      header={header}
      left={<Feed emptyState={() => <EmptyState name={emptyStateName} />} variables={variables} />}
      right={
        <Grid container direction="column" spacing={3}>
          {leaderboard && (
            <Grid item xs>
              <LeaderboardWidget variables={{ user_id: profile.id }} />
            </Grid>
          )}
          <Grid item xs>
            <Queries.GetSocialConnections loader="widget" variables={{ user_id: profile.id }}>
              {connections => renderContact(connections)}
            </Queries.GetSocialConnections>
          </Grid>
          {renderFollowing()}
          {renderGroups()}
          {renderFollowers()}
        </Grid>
      }
    />
  );
};

export default ProfileActivity;
